import { ApiEpConfig } from "@smp/api";
import { REACT_APP__DEBUG } from "@smp/utils";
import { QueryClient } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Outlet, RouteObject } from "react-router-dom";

import { ErrorBoundary } from "@/components/feedback/errors";
import { RouteErrorBoundary } from "@/components/feedback/route_error_boundary";
import { ProviderConfirm } from "@/context/confirm";
import { ProviderConfirmComponent } from "@/context/confirm_component";
import { ProviderGtm } from "@/context/gtm";
import { ProviderIndexedDb } from "@/context/indexed_db";
import { ProviderLanguage } from "@/context/language";
import { ProviderLoader } from "@/context/loader";
import { ProviderSnackbar } from "@/context/snackbar";
import { ProviderSupportMessenger } from "@/context/support_messenger";
import { ProviderUi } from "@/context/ui";
import { ProviderURLSearchParams } from "@/context/url_search_params";
import { messagesLoader } from "@/loaders/messages_loader";
import { Layout } from "@/screens/_layout";
import { App } from "@/screens/app";
import { Stickers } from "@/screens/app_public/stickers";
import { Templates } from "@/screens/app_public/templates";
import { Auth } from "@/screens/auth";
import { Graphql } from "@/screens/graphql";
import { OAuth } from "@/screens/oauth";
import { Public } from "@/screens/public";
import { Shared } from "@/screens/shared";

import { ProviderEvents } from "./context/events";
import { ELocale } from "./context/language/language.types";
import { KEY as KEY_CONFIG } from "./hooks/use_ep_config";

const createRoutes = (
  queryClient: QueryClient,
  locale?: ELocale,
): RouteObject[] => [
  {
    path: "/*",
    element: (
      <ProviderURLSearchParams>
        <ProviderUi>
          <ProviderLanguage>
            <ErrorBoundary>
              <ProviderLoader>
                <ProviderSupportMessenger>
                  <ProviderConfirm>
                    <ProviderSnackbar>
                      <ProviderGtm>
                        <ProviderConfirmComponent>
                          <ProviderIndexedDb>
                            <ProviderEvents>
                              <Outlet />
                            </ProviderEvents>
                          </ProviderIndexedDb>
                        </ProviderConfirmComponent>
                      </ProviderGtm>

                      {REACT_APP__DEBUG && <ReactQueryDevtools />}
                    </ProviderSnackbar>
                  </ProviderConfirm>
                </ProviderSupportMessenger>
              </ProviderLoader>
            </ErrorBoundary>
          </ProviderLanguage>
        </ProviderUi>
      </ProviderURLSearchParams>
    ),
    loader: async () => {
      const [messagesData] = await Promise.all([
        messagesLoader(locale),
        queryClient.prefetchQuery(KEY_CONFIG, ApiEpConfig.retrieve),
      ]);

      return messagesData;
    },
    shouldRevalidate: () => false,
    children: [
      {
        path: "o/authorize",
        element: <OAuth />,
      },
      {
        path: "auth/*",
        element: <Auth />,
      },
      {
        path: "graphql",
        element: <Graphql />,
      },
      {
        path: "public/*",
        element: <Public />,
      },
      {
        path: "shared/*",
        element: <Shared />,
      },
      {
        path: "templates/:templateId?/*",
        element: (
          <Layout isAuthReuired={false}>
            <Templates pathPattern="/templates/:templateId?/*" />
          </Layout>
        ),
      },
      {
        path: "stickers/:stickerId?/*",
        element: (
          <Layout isAuthReuired={false}>
            <Stickers pathPattern="/stickers/:stickerId?/*" />
          </Layout>
        ),
      },
      {
        path: "*",
        element: (
          <Layout isAuthReuired={true}>
            <App />
          </Layout>
        ),
      },
    ].map((route) => ({
      ...route,
      errorElement: <RouteErrorBoundary />,
    })),
  },
];

export default createRoutes;
